/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.refreshComplementaryUpsell();
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.refreshComplementaryUpsell();
    });
    this.addEventListener('submit', this.handleUpsellSubmit.bind(this));
    this.addEventListener('change', this.handleUpsellVariantChange.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }

  refreshComplementaryUpsell() {
    this.querySelectorAll('.cart-drawer__complementary-upsell carousel-slider').forEach((el) => {
      if (typeof el.refresh === 'function') el.refresh();
    });
  }

  handleUpsellVariantChange(evt) {
    const select = evt.target.closest('[data-complementary-upsell-variants]');
    if (!select) return;

    const card = select.closest('.cart-drawer__upsell-card');
    const option = select.selectedOptions[0];
    if (!card || !option) return;

    const priceEl = card.querySelector('[data-upsell-price]');
    if (priceEl && option.dataset.price) priceEl.textContent = option.dataset.price;

    const img = card.querySelector('.cart-drawer__upsell-card-media img');
    if (img && option.dataset.image) {
      img.src = option.dataset.image;
      img.removeAttribute('srcset');
      img.removeAttribute('data-manual-src');
      img.removeAttribute('data-manual-srcset');
    }

    const addBtn = card.querySelector('[name="add"]');
    if (!addBtn) return;

    const available = option.dataset.available !== 'false';
    addBtn.disabled = !available;
    if (available) {
      addBtn.removeAttribute('aria-disabled');
    } else {
      addBtn.setAttribute('aria-disabled', 'true');
    }
  }

  async handleUpsellSubmit(evt) {
    const form = evt.target.closest('[data-complementary-upsell-form]');
    if (!form) return;

    evt.preventDefault();
    evt.stopPropagation();

    const submitBtn = form.querySelector('[name="add"]');
    if (!submitBtn || submitBtn.getAttribute('aria-disabled') === 'true' || submitBtn.disabled) return;

    const errorEl = form.querySelector('.js-form-error');
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    submitBtn.setAttribute('aria-disabled', 'true');
    submitBtn.classList.add('is-loading');

    const formData = new FormData(form);
    const sectionsToUpdate = ['page-header', 'cart-drawer']
      .map((sel) => document.querySelector(sel))
      .filter((el) => el);
    const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);

    formData.append('sections_url', window.location.pathname);
    formData.append('sections', sectionIds.join(','));

    try {
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      const data = await response.json();
      let error = typeof data.description === 'string' ? data.description : data.message;
      if (data.errors && typeof data.errors === 'object') {
        error = Object.entries(data.errors).map((item) => item[1].join(', '));
      }
      if (data.status && errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = Array.isArray(error) ? error.join(', ') : error;
      }
      if (!response.ok) throw new Error(error || response.status);

      if (data.sections && sectionsToUpdate.length === Object.keys(data.sections).length) {
        sectionsToUpdate.forEach((el) => {
          if (typeof el.updateFromCartChange === 'function') {
            el.updateFromCartChange(data.sections[el.dataset.sectionId]);
          }
        });
      } else {
        this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      }

      this.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { variantId: data.variant_id }
      }));
    } catch (error) {
      console.log(error); // eslint-disable-line
      this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
    } finally {
      submitBtn.classList.remove('is-loading');
      submitBtn.removeAttribute('aria-disabled');
    }
  }
}

window.customElements.define('cart-drawer', CartDrawer);
